import { useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * useBackendWakeUp
 *
 * The backend FastAPI container running on Kubernetes can scale to zero
 * (sleep) during periods of inactivity. When a visitor first opens the
 * website, the static frontend loads instantly from the CDN, but any
 * request to the API (including image requests like /api/uploads/...)
 * may fail with 502/504 while the backend is waking up.
 *
 * This hook aggressively pings the /api/health endpoint as soon as the
 * app mounts, forcing the container to wake before the user scrolls
 * to any product images. It retries automatically until success.
 */
export function useBackendWakeUp() {
  const wakeSuccessRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let attempt = 0;

    const ping = async () => {
      if (cancelled || wakeSuccessRef.current) return;

      attempt += 1;
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000);

        const res = await fetch(`${API_URL}/api/health`, {
          method: 'GET',
          cache: 'no-store',
          signal: controller.signal,
          credentials: 'omit',
        });

        clearTimeout(timeoutId);

        if (res.ok) {
          wakeSuccessRef.current = true;
          // Dispatch a custom event so image components can retry if needed
          window.dispatchEvent(new CustomEvent('backend-awake'));
          return;
        }
      } catch (_err) {
        // backend is still waking up, keep retrying
      }

      // Retry with capped exponential-ish backoff
      const delay = Math.min(800 * attempt, 4000);
      if (!cancelled && attempt < 20) {
        setTimeout(ping, delay);
      }
    };

    // Fire immediately on mount
    ping();

    return () => {
      cancelled = true;
    };
  }, []);
}
